import { jsx as _jsx } from "react/jsx-runtime";
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../lib/cn';
/**
 * Two tab shapes, because the design uses both for different jobs.
 *
 * `pill` is the segmented control — a small closed set of peer views over the
 * same object, where the group reads as one control. The admin console's
 * Users / Audit log switch is this (`ADM-USR-014`).
 *
 * `underline` is section navigation within a record, where the tabs are a row
 * of destinations rather than a switch.
 *
 * **`underline` scrolls rather than overflowing.** Its triggers are `whitespace-nowrap`, the list
 * is `inline-flex` with a 24px gap, and neither wraps — so six section tabs have a fixed 647px
 * intrinsic width, and on a 375px screen the last three sat off the right edge of the *page*,
 * taking the whole layout with them. Every settings route and the event console had a horizontal
 * scrollbar because of it (organizer sweep, 2026-09-12).
 *
 * `max-w-full` gives the list something to be narrower than, and `overflow-x-auto` makes the
 * overflow the strip's own rather than the document's — the treatment Discover's atmosphere chips
 * already use for the same shape. The scrollbar itself is hidden, because a row of section links
 * with a visible bar under it reads as a broken border; the row still scrolls by wheel, by drag and
 * by keyboard, and focusing a tab scrolls it into view, which is what a keyboard user needs.
 *
 * `pill` is left alone: it is a small closed set and is not near a width where this bites.
 */
const listVariants = cva('inline-flex items-center', {
    variants: {
        variant: {
            pill: 'gap-1 rounded-pill bg-muted p-1',
            underline: 'max-w-full gap-6 overflow-x-auto border-b border-border [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        },
    },
    defaultVariants: { variant: 'pill' },
});
const triggerVariants = cva(
// The focus ring is on the trigger rather than the list: a keyboard user
// needs to see which tab they are on, not that the group has focus.
'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50', {
    variants: {
        variant: {
            pill: 'rounded-pill px-4 py-1.5 text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-xs',
            underline: '-mb-px border-b-2 border-transparent px-1 pb-3 text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground',
        },
    },
    defaultVariants: { variant: 'pill' },
});
const VariantContext = React.createContext('pill');
export const Tabs = TabsPrimitive.Root;
export const TabsList = React.forwardRef(({ className, variant, ...props }, ref) => (
// The variant is shared through context so each trigger does not have to
// repeat it and cannot disagree with its own list.
_jsx(VariantContext.Provider, { value: variant ?? 'pill', children: _jsx(TabsPrimitive.List, { ref: ref, className: cn(listVariants({ variant }), className), ...props }) })));
TabsList.displayName = TabsPrimitive.List.displayName;
export const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => {
    const variant = React.useContext(VariantContext);
    return (_jsx(TabsPrimitive.Trigger, { ref: ref, className: cn(triggerVariants({ variant }), className), ...props }));
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
export const TabsContent = React.forwardRef(({ className, ...props }, ref) => (_jsx(TabsPrimitive.Content, { ref: ref, className: cn('mt-6 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', className), ...props })));
TabsContent.displayName = TabsPrimitive.Content.displayName;
//# sourceMappingURL=tabs.js.map